/**
 * Policy page content. Each policy has title, lastUpdated, and sections (heading + paragraphs).
 * POLICY_CONTENT keys are the policy slugs; POLICY_NAV gives display labels.
 */

const POLICY_NAV = {
  terms: 'Terms & Conditions',
  privacy: 'Privacy Policy',
  'refund-policy': 'Refund & Cancellation Policy',
  'service-delivery-policy': 'Service Delivery Policy',
  about: 'About Us',
  disclaimer: 'Disclaimer',
  'pricing-payment-policy': 'Pricing & Payment Policy',
  careers: 'Careers'
};

function section(heading, ...paragraphs) {
  return { heading, paragraphs };
}

const POLICY_CONTENT = {
  terms: {
    title: 'Terms & Conditions',
    lastUpdated: '2025',
    sections: [
      section(
        '1. Acceptance of Terms',
        'These Terms and Conditions outline the legal agreements between ApkaKhata and its customers. By accessing and using the ApkaKhata website and services, you accept and agree to be bound by these terms. If you do not agree with any part of these terms, you must not use our website or services. We recommend that you read these terms carefully and retain a copy for your records. Your continued use of the platform after any updates constitutes your acceptance of the revised terms.'
      ),
      section(
        '2. Service and Product Details',
        'We offer website development, business software, and digital solutions designed to help businesses establish their online presence, streamline operations, and grow. Our services and products are described on our website and in your service agreement or order confirmation.'
      ),
      section(
        '3. Payment Terms, Usage Restrictions & Intellectual Property Ownership',
        'Payments for our services and products are due as specified in your order or agreement (e.g., monthly, annually, or advance and balance on completion for projects) and must be made via the methods we accept (e.g., UPI, card, net banking). Failure to make timely payments may result in suspension or termination of services.',
        'Users may not use our services or products for unlawful purposes or in any manner that could harm our operations or reputation. Unauthorized use or distribution of our intellectual property is strictly prohibited.',
        'All content, including text, graphics, logos, and software, is the property of ApkaKhata and is protected by intellectual property laws. Users are granted a limited, non-exclusive license to use our services and products for personal or business use in accordance with these terms.'
      ),
      section(
        '4. Use of Services',
        'You agree to use our services only for lawful purposes and in accordance with these terms. You must not use our services in any way that could damage, disable, or impair the platform or interfere with others\' use. You may not attempt to gain unauthorized access to any part of the system, other accounts, or any connected network. You must not transmit any viruses, malware, or other harmful code. We reserve the right to suspend or terminate access for any use we deem to be in violation of these terms or harmful to other users or the platform.'
      ),
      section(
        '5. User Responsibilities',
        'Users are responsible for providing accurate information, maintaining the confidentiality of their account, and complying with applicable laws while using our services. You are responsible for all activities under your account and must notify us immediately of any unauthorized use. You must provide accurate and complete information when registering and keep your details up to date. We may suspend or close accounts that are used in breach of these terms or that remain inactive for an extended period as per our internal policies.'
      ),
      section(
        '6. Additional Payment and Fee Terms',
        'All amounts are in Indian Rupees unless otherwise stated. We may change pricing with reasonable notice; such changes will not affect orders or subscriptions already confirmed. Where permitted by law, we may charge interest on overdue amounts.'
      ),
      section(
        '7. Limitation of Liability',
        'ApkaKhata is not liable for any indirect, incidental, or consequential damages arising from the use or inability to use our services or products. Our total liability is limited to the amount paid by the user for the specific service or product in question. We do not warrant that the services will be uninterrupted or error-free. Some jurisdictions do not allow certain limitations; where applicable, our liability will be limited to the maximum extent permitted by law.'
      ),
      section(
        '8. Termination',
        'We may suspend or terminate your access to the services at any time for breach of these terms, non-payment, or for any other reason we consider necessary. You may stop using the services at any time. Upon termination, your right to use the platform ceases immediately. Provisions that by their nature should survive termination (including intellectual property, limitation of liability, and governing law) will remain in effect.'
      ),
      section(
        '9. Dispute Resolution',
        'Any disputes arising out of or related to these terms or the use of our services or products will be resolved through arbitration or mediation in India. Users agree to first attempt to resolve any issues informally by contacting us via the Contact Us page or the email and phone numbers provided on our website. If the dispute cannot be resolved within a reasonable period, it may be referred to mediation or arbitration as agreed by the parties, or otherwise in accordance with applicable law. Nothing in this clause prevents either party from seeking urgent injunctive or other equitable relief from a court of competent jurisdiction.'
      ),
      section(
        '10. Governing Law',
        'These Terms and Conditions are governed by the laws of India. Any legal proceedings shall be subject to the exclusive jurisdiction of the courts in India. If you access our services from outside India, you are responsible for compliance with local laws to the extent they apply.'
      ),
      section(
        '11. Indemnification',
        'You agree to indemnify, defend, and hold harmless ApkaKhata, its management, and its affiliates from and against any claims, damages, losses, or expenses (including reasonable legal fees) arising out of or related to your use of the services, your breach of these terms, or your violation of any law or third-party rights.'
      ),
      section(
        '12. Severability',
        'If any provision of these terms is found to be invalid or unenforceable by a court of competent jurisdiction, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall continue in full force and effect.'
      ),
      section(
        '13. Changes',
        'We reserve the right to modify these terms at any time. We will notify users of material changes by posting the updated terms on our website and, where appropriate, by email or other reasonable means. Your continued use of the services after the effective date of changes constitutes acceptance of the revised terms. If you do not agree to the new terms, you must stop using our services.'
      ),
      section(
        '14. Management',
        'ApkaKhata is operated and managed by a single responsible person. The overall direction, day-to-day operations, and strategic decisions of the business are under Amit Kumar. All customer communications, service delivery, and policy matters are overseen under this management. For any queries relating to these terms, service agreements, or the conduct of the business, you may reach out through the official contact channels listed on our website.'
      ),
      section(
        '15. Contact',
        'For questions about these Terms and Conditions, please use the Contact Us page or the email address provided on our website. We aim to respond to legitimate inquiries within a reasonable time.'
      )
    ]
  },

  privacy: {
    title: 'Privacy Policy',
    lastUpdated: '2025',
    sections: [
      section(
        '1. Information We Collect',
        'We collect information you provide directly (name, email, phone, business details), usage data when you use our platform, and technical data such as IP address and browser type.'
      ),
      section(
        '2. How We Use Your Information',
        'We use your information to provide and improve our services, process transactions, send important updates, respond to inquiries, and for analytics to improve user experience.'
      ),
      section(
        '3. Data Sharing',
        'We do not sell your personal information. We may share data with trusted service providers who assist in operating our platform, and when required by law.'
      ),
      section(
        '4. Data Security',
        'We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, or destruction.'
      ),
      section(
        '5. Your Rights',
        'You may request access to, correction of, or deletion of your personal data. You may also opt out of marketing communications at any time.'
      ),
      section(
        '6. Cookies',
        'We use cookies and similar technologies to enhance your experience and analyze usage. You can manage cookie preferences in your browser settings.'
      )
    ]
  },

  'refund-policy': {
    title: 'Refund & Cancellation Policy',
    lastUpdated: '2025',
    sections: [
      section(
        '1. Purpose of This Policy',
        'This Refund & Cancellation page outlines the guidelines and procedures for refunds and cancellations of orders or services offered by ApkaKhata. Please read this policy carefully before making a purchase.'
      ),
      section(
        '2. Refund Eligibility',
        'ApkaKhata generally does not offer refunds once a transaction is completed. In exceptional cases where we agree to consider a refund, you must contact us within 7 days of payment or service delivery, and your request will be evaluated on a case-by-case basis. Eligibility does not guarantee a refund.'
      ),
      section(
        '3. Refund Timeline',
        'If a refund is approved, it will be processed within 7 to 8 working days after we confirm eligibility and complete the necessary verification. The amount will be credited to the original source of payment.'
      ),
      section(
        '4. Return or Replacement Process',
        'To request a return, replacement, or adjustment for any product or service, please contact us via the Contact Us page or the email provided on our website and follow the instructions we provide. For physical items (if any), the product must be in the same condition as when you received it. For digital services, we will assess each request according to this policy.'
      ),
      section(
        '5. Non-Refundable and Non-Cancellable Items',
        'Some products or services cannot be refunded or cancelled once the transaction is completed or work has started. These include: custom-made or custom-developed work (e.g., websites, software) once delivery has begun or been completed; digital products and downloads; subscriptions and packages once the transaction is completed; and services already rendered. All payments for such items are final.'
      ),
      section(
        '6. Cancellations',
        'You may cancel a subscription or recurring service according to the terms of your plan. Cancellation will stop future charges but does not entitle you to a refund for amounts already paid.'
      ),
      section(
        '7. Cancellation Process',
        'To cancel an order or service, contact us via the Contact Us page or the email provided on our website before work has started or the order is processed. Once work has started or the order is in progress, cancellation may not be possible, or you may need to discuss options with us. Provide your order number and reason for cancellation when you contact us.'
      ),
      section(
        '8. Cancellation Fee',
        'A cancellation fee may apply if the order or project is already being processed. The applicable amount or percentage (if any) will be communicated at the time of cancellation, depending on the work completed and our internal policies.'
      ),
      section(
        '9. Requirements for Cancellation',
        'When requesting a cancellation, please provide your order number and the reason for cancellation. This helps us process your request promptly and, where applicable, consider any exception in line with this policy.'
      ),
      section(
        '10. Service Commitment',
        'We are committed to delivering the services as described. If you experience technical issues or service failures, please contact us and we will work to resolve the issue in accordance with our Service Delivery Policy.'
      ),
      section(
        '11. Exceptions',
        'Any exception to this refund and cancellation policy would require prior written agreement from ApkaKhata and would be considered on a case-by-case basis only where required by applicable law or at our sole discretion.'
      ),
      section(
        '12. Contact',
        'For questions about this policy, refund requests, or cancellations, please contact us via the Contact Us page or the email provided on our website. We aim to respond to legitimate inquiries within a reasonable time.'
      )
    ]
  },

  'service-delivery-policy': {
    title: 'Service Delivery Policy',
    lastUpdated: '2025',
    sections: [
      section(
        '1. Scope',
        'This policy describes how ApkaKhata delivers its software, digital solutions, and related services to customers.'
      ),
      section(
        '2. Delivery Timeline',
        'Delivery timelines are communicated at the time of purchase or in your project agreement. We strive to meet all stated deadlines and will keep you informed of any changes.'
      ),
      section(
        '3. Access and Onboarding',
        'Upon payment confirmation, you will receive access credentials and onboarding instructions. For custom projects, we will schedule kick-off and handover as per the agreed plan.'
      ),
      section(
        '4. Support',
        'We provide support as per your package or agreement. Support channels and response times are outlined in your service agreement or package details.'
      ),
      section(
        '5. Delays',
        'In case of unforeseen delays, we will notify you and work to minimize impact. We are not liable for delays caused by factors outside our reasonable control.'
      )
    ]
  },

  about: {
    title: 'About Us',
    lastUpdated: '2025',
    sections: [
      section(
        'Who We Are',
        'ApkaKhata provides business software, digital solutions, and professional services to help businesses streamline operations and grow. We focus on practical, scalable solutions tailored to our clients\' needs.'
      ),
      section(
        'Our Mission',
        'Our mission is to empower businesses with reliable technology and support, from accounting and inventory to custom software and digital presence.'
      ),
      section(
        'What We Offer',
        'We offer packaged solutions (e.g., website packages, business software), custom development, and ongoing support. Our services are designed for small to medium businesses as well as enterprises where applicable.'
      ),
      section(
        'Contact',
        'For more information or to get started, visit our Contact Us page. We are happy to answer your questions and discuss how we can help your business.'
      )
    ]
  },

  disclaimer: {
    title: 'Disclaimer',
    lastUpdated: '2025',
    sections: [
      section(
        '1. General',
        'The information on the ApkaKhata website and in our communications is provided for general information purposes only. We do not guarantee that the information is complete, current, or suitable for your specific situation.'
      ),
      section(
        '2. No Professional Advice',
        'Content on this site does not constitute legal, tax, or financial advice. You should consult qualified professionals for advice tailored to your circumstances.'
      ),
      section(
        '3. Service Outcomes',
        'Results from our services may vary depending on your use, data, and external factors. We do not guarantee specific business outcomes, revenue, or performance metrics unless explicitly agreed in writing.'
      ),
      section(
        '4. Third-Party Links',
        'Our site may contain links to third-party websites. We are not responsible for the content or practices of those sites. Use of third-party services is at your own risk.'
      ),
      section(
        '5. Limitation',
        'To the fullest extent permitted by law, ApkaKhata disclaims all warranties and limits its liability as set out in our Terms & Conditions.'
      )
    ]
  },

  'pricing-payment-policy': {
    title: 'Pricing & Payment Policy',
    lastUpdated: '2025',
    sections: [
      section(
        '1. Pricing',
        'Prices for our packages and services are displayed on our website and in quotes. All prices are in Indian Rupees (₹) unless otherwise stated. We reserve the right to update pricing; changes will not affect orders already confirmed.'
      ),
      section(
        '2. Payment Methods',
        'We accept payment through the methods indicated at checkout (e.g., card, UPI, net banking). Payment must be completed as per the checkout flow to confirm your order.'
      ),
      section(
        '3. Invoices and Receipts',
        'After successful payment, you will receive a confirmation and receipt. Invoices can be accessed from your account or requested from our support team.'
      ),
      section(
        '4. Taxes',
        'Prices may be inclusive or exclusive of applicable taxes (e.g., GST) as indicated at the time of purchase. You are responsible for any taxes applicable in your jurisdiction where required.'
      ),
      section(
        '5. Failed or Disputed Payments',
        'If a payment fails, we may contact you to complete the transaction. Disputed payments may result in suspension of access until the dispute is resolved. Chargebacks may lead to account review and possible termination of services.'
      )
    ]
  }
};

/** Footer link order: policy slugs plus Contact Us (not under /legal) */
const FOOTER_LINK_ORDER = [
  'terms',
  'privacy',
  'refund-policy',
  'service-delivery-policy',
  'contact',
  'careers',
  'about',
  'disclaimer',
  'pricing-payment-policy'
];

export function getPolicyContent(slug) {
  return POLICY_CONTENT[slug] || null;
}

export function getPolicyNav() {
  return POLICY_NAV;
}

export function getPolicySlugs() {
  return Object.keys(POLICY_CONTENT);
}

export function getPolicyTitle(slug) {
  return POLICY_NAV[slug] || POLICY_CONTENT[slug]?.title || slug;
}

/** Returns { to, label }[] for footer links in display order. */
export function getFooterPolicyLinks() {
  return FOOTER_LINK_ORDER.map((slug) => {
    if (slug === 'contact') return { to: '/contact', label: 'Contact Us' };
    if (slug === 'careers') return { to: '/careers', label: 'Careers' };
    return { to: `/legal/${slug}`, label: POLICY_NAV[slug] || slug };
  });
}
